// Mantine
import { Badge, Text, useComputedColorScheme } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';

// Icons
import {
  IconCloudCheck,
  IconCloudUpload,
  IconCloudX,
} from '@tabler/icons-react';

// Interfaces
import {
  SubmissionStatus as Status,
  Submission,
  SubmissionWithCount,
} from 'interfaces/submission';

// Utils
import { getSubmissionStatusChipPresentation } from 'utils/badgeColors';

// Custom Components
import { DataBadge } from 'components/common/DataBadge';

import { useCurrentSiteStore } from 'store';
import { formatDateTime } from 'utils/dateUtils';

export const SubmissionColumns = () => {
  const colorScheme = useComputedColorScheme('dark');

  const { timezone } = useCurrentSiteStore();

  const columns: MRT_ColumnDef<Submission>[] = [
    {
      accessorKey: 'filename',
      header: 'File Name',
      accessorFn: (row) => {
        const chunks = row?.filename?.split('/');
        if (!chunks) {
          return undefined;
        }
        const base = chunks[chunks?.length - 1];
        return base;
      },
      Cell: ({ cell }) => <Text fw="bold">{cell.getValue() as string}</Text>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Creation Date',
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        if (!value) {
          return 'N/A';
        }
        return formatDateTime(value, { timezone, fallback: 'N/A' });
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      maxSize: 100,
      accessorFn: (row) => row?.status,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        const statusChip = getSubmissionStatusChipPresentation(
          value,
          colorScheme
        );
        return (
          <Badge
            variant="light"
            size="md"
            radius="sm"
            style={{
              backgroundColor: statusChip.backgroundColor,
              border: `1px solid ${statusChip.borderColor}`,
              color: statusChip.textColor,
            }}
          >
            {value}
          </Badge>
        );
      },
    },
    {
      header: 'Claim Count',
      maxSize: 75,
      accessorFn: (row) => {
        const castedRow = row as SubmissionWithCount;
        return castedRow?.claimCount;
      },
      Cell: ({ cell }) => {
        const claims = cell.getValue() as number;
        return <DataBadge>{claims}</DataBadge>;
      },
    },
    {
      accessorKey: 'uploaded',
      header: 'Uploaded',
      minSize: 50,
      maxSize: 120,
      size: 60,
      Cell: ({ cell }) => {
        return (
          <>
            {cell.row.original.status === Status.cancelled && (
              <IconCloudX
                style={{
                  color:
                    cell.row.original.status === Status.cancelled
                      ? 'red'
                      : 'gray',
                }}
              />
            )}

            {cell.row.original.uploaded &&
              (cell.row.original.status === Status.submitted ||
                cell.row.original.status === Status.received) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <IconCloudCheck style={{ color: 'green' }} />
                </div>
              )}

            {cell.row.original.status === Status.created && (
              <IconCloudUpload style={{ color: 'gray' }} />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'submittedAt',
      header: 'Submitted At',
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        if (!value) {
          return 'N/A';
        }
        return formatDateTime(value, {
          timezone,
          fallback: 'N/A',
        });
      },
    },
  ];
  return columns;
};
